<script setup lang="ts">
import { computed } from 'vue';
import { AkButton, AkIcon, AkHighlight, AkTooltipInformation } from '@ankorstore/design-system';
import { useI18n } from 'vue-i18n';
import QuantityCounter from '../quantity-counter/quantity-counter.vue';
import LoaderIcon from '@monolith/legacy/components/loader-icon.vue';
import type { QuantityCounterSource } from '@monolith/legacy/services/analytics/events/quantity-counter-interacted-event';
import { useQatcHighlight } from '@modules/shared/composables/use-qatc-highlight';

const props = defineProps<{
  quantity: number;
  unitMultiplier: number;
  isLoading: boolean;
  userIsBrand: boolean;
  confirmation: boolean;
  productId: number;
  presetQuantity?: boolean;
  onDrawer?: boolean;
  showHighlight?: boolean;
  /** Quick-view redesign only: shown next to a cart-plus icon instead of the plain cart icon. */
  priceLabel?: string;
}>();

const emit = defineEmits<{
  'update:quantity': [value: number];
  'addClick': [event: Event];
}>();

const { t } = useI18n();

const { highlightVisible, dismissHighlight } = useQatcHighlight(props.showHighlight ?? false);

const isAddDisabled = computed(() => props.quantity === 0 || props.userIsBrand || props.isLoading);

const trackingSource = computed<QuantityCounterSource>(() => {
  if (!props.presetQuantity) {return 'catalog_card';}
  return props.onDrawer ? 'product_drawer' : 'product_page';
});
</script>

<template>
  <div class="qatc-single-variant">
    <template v-if="!confirmation">
      <AkTooltipInformation
        :is-enabled="highlightVisible"
        :show="highlightVisible"
        trigger="manual"
        close-button
        type="invert"
        placement="top"
        custom-class="qatc-highlight"
        :title="t('discovery.catalog.qatcHighlight.title')"
        :content="t('discovery.catalog.qatcHighlight.content')"
        @close="dismissHighlight"
      >
        <AkHighlight
          :enable="highlightVisible"
          rounded="md"
          @click="dismissHighlight"
        >
          <QuantityCounter
            :model-value="quantity"
            :step="unitMultiplier"
            :min="0"
            :disabled="userIsBrand"
            :tracking-context="{ source: trackingSource, productId }"
            class="qatc-single-variant__counter"
            @update:model-value="emit('update:quantity', $event)"
          />
        </AkHighlight>
      </AkTooltipInformation>
      <AkButton
        data-testid="addToCartButton"
        size="lg"
        color="primary"
        :symbol="priceLabel ? 'cart-plus' : 'cart'"
        :disabled="isAddDisabled"
        @click="emit('addClick', $event)"
      >
        <LoaderIcon
          v-if="isLoading"
          :dark="true"
          :small="true"
        />
        <template v-else-if="priceLabel">
          {{ priceLabel }}
        </template>
      </AkButton>
    </template>

    <AkButton
      v-else
      size="lg"
      color="success"
      class="qatc-single-variant__confirmation"
      data-testid="confirmationButton"
    >
      <AkIcon
        size="md"
        symbol="check"
        color="white"
        class="qatc-single-variant__confirmation-icon"
      />
      {{ quantity }} {{ t('discovery.qatc.added') }}
    </AkButton>
  </div>
</template>

<style lang="scss" scoped>
.qatc-single-variant {
  @apply ds-flex ds-gap-2 ds-mt-3 ds-items-center ds-justify-center md:ds-justify-start ds-w-full;

  &__confirmation {
    @apply ds-w-full;
  }

  &__confirmation-icon {
    @apply ds-mr-2;
  }

  button {
    @apply ds-relative;

    .loader-icon {
      @apply ds-relative ds-top-0 ds-left-0;
    }
  }
}
</style>

<style lang="scss">
// AkTooltipInformation teleports its popper to <body> with z-index: 10000.
// Scope it down so it doesn't overlay the nav or drawers.
.ak-tooltip.ak-tooltip--qatc-highlight.v-popper__popper {
  @apply ds-z-10;
}
</style>
